import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  listAuthorisationsStart,
  clearError
} from 'redux/authorisation/actions';
import {
  selectAuthorisations,
  selectAuthorisationProcess,
  selectAuthorisationTypesOption,
  selectTotalAuthorisations
} from 'redux/authorisation/selectors';
import {
  Tag,
  Link,
  Button,
  DataTable,
  DataTableSkeleton,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableContainer,
  TableExpandRow,
  TableExpandedRow,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Pagination,
  DataTableCell,
  DenormalizedRow,
  DataTableCustomRenderProps
} from 'carbon-components-react';
import { Add16 } from '@carbon/icons-react';
import { Params, AuthorisationItem } from 'types';
import { toLocalDateFormat, debounce } from 'utilities/helper.util';
import OrderTable from './orderTable';
import DetailsTable from './detailsTable';
type Props = {
  onAddAuthorisation: Function;
  onEditAuthorisation: Function;
  onAddOrder: Function;
  refreshCounter: number;
};
const PAGE_LIMIT = 10;
const PAGE_OFFSET = 0;
const PageSizes = [10, 20, 50];

const ListView: React.FC<Props> = ({
  onAddAuthorisation,
  onAddOrder,
  onEditAuthorisation,
  refreshCounter
}) => {
  const dispatch = useDispatch();
  const categoryListItems = useSelector(selectAuthorisationTypesOption) || [];
  const authorisations = useSelector(selectAuthorisations);
  const authorisationProcess = useSelector(selectAuthorisationProcess);
  const totalAuthorisations = useSelector(selectTotalAuthorisations);
  const [pageOffset, setPageOffset] = useState<number>(PAGE_OFFSET);
  const [pageSize, setPageSize] = useState<number>(PAGE_LIMIT);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowData, setRowData] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [filterText, setFilterText] = useState<string>('');
  const [sortingHeader, setSortingHeader] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC' | 'NONE'>(
    'ASC'
  );
  const [orderBy, setOrderBy] = useState<string>('name');
  const headers = [
    { key: 'name', header: 'Name', isSortable: true },
    { key: 'authorisationType', header: 'Authorised for', isSortable: false },
    { key: 'transactionsLeft', header: 'Transactions left', isSortable: true },
    { key: 'expires', header: 'Expires', isSortable: true },
    { key: 'action', header: '', isSortable: false }
  ];
  const handleEditClick = (id: number) => {
    onEditAuthorisation(id);
  };
  const handlePageChange = ({
    page,
    pageSize
  }: {
    page: number;
    pageSize: number;
  }) => {
    setPageOffset((page - 1) * pageSize);
    setCurrentPage(page);
    setPageSize(pageSize);
  };
  const handleSearch = (e: any) => {
    setSearchText(e.target.value);
    debouncedFunction(e.target.value);
  };
  const handleChangeSearch = (searchText: string) => {
    setPageOffset(PAGE_OFFSET);
    setCurrentPage(1);
    setFilterText(searchText);
  };

  const onClickHeader = (key: string) => {
    const direction =
      key === sortingHeader
        ? sortDirection === 'DESC'
          ? 'ASC'
          : 'DESC'
        : 'DESC';
    const order = `${direction === 'DESC' ? '-' : ''}${key}`;
    setSortingHeader(key);
    setOrderBy(order);
    setSortDirection(direction);
  };

  const authorisationTypeTextMapping = (type: string) => {
    const authorisationType = categoryListItems.find(
      (option) => option.value === type
    );
    if (authorisationType) {
      return authorisationType.label;
    }

    return 'Unknown';
  };

  const renderCell = (
    cell: DataTableCell,
    row: DenormalizedRow,
    authorisations: AuthorisationItem[]
  ) => {
    switch (cell.info.header) {
      case 'expires':
        return toLocalDateFormat(cell.value);
      case 'authorisationType':
        return authorisationTypeTextMapping(cell.value);
      case 'name': {
        const authorisation = authorisations.find(
          (a) => String(a.id) === row.id
        );
        if (authorisation?.deletedAt !== null) {
          return (
            <div className="authorisation-name-tag-container">
              {cell.value}
              <Tag type="red">Suspended</Tag>
            </div>
          );
        } else {
          return cell.value;
        }
      }
      case 'action':
        return (
          <Link onClick={() => handleEditClick(Number(row.id))}>Edit</Link>
        );
      default:
        return cell.value;
    }
  };
  const debouncedFunction = useCallback(
    debounce(async (args: string[]) => {
      handleChangeSearch(args[0]);
    }, 1000),
    []
  );
  useEffect(() => {
    const queryParams: Params = {
      fields:
        'id,name,authorisationType,deletedAt,externalId,channel,primaryContactName,primaryContactEmail,transactionsLeft,expires',
      include: 'Orders',
      order: orderBy,
      limit: pageSize,
      offset: pageOffset,
      filter: `deleted:true${filterText ? `,name:%${filterText}%` : ''}`
    };
    dispatch(listAuthorisationsStart(queryParams));
  }, [dispatch, pageOffset, filterText, refreshCounter, pageSize, orderBy]);

  useEffect(() => {
    const rows = authorisations.map((a) => {
      const { id, name, transactionsLeft, expires, authorisationType } = a;
      return {
        id: String(id),
        name,
        authorisationType,
        expires,
        transactionsLeft
      };
    });
    setRowData(rows);
  }, [authorisations]);
  return (
    <>
      {authorisationProcess === 'listing' || categoryListItems.length === 0 ? (
        <DataTableSkeleton showHeader={false} columnCount={5} rowCount={1} />
      ) : (
        <>
          <DataTable rows={rowData} headers={headers}>
            {({
              rows,
              headers,
              getHeaderProps,
              getRowProps,
              getTableProps
            }: DataTableCustomRenderProps) => (
              <TableContainer>
                <TableToolbar>
                  <TableToolbarContent>
                    <TableToolbarSearch
                      onChange={handleSearch}
                      value={searchText}
                      expanded={searchText ? true : undefined}
                    />
                    <Button
                      renderIcon={Add16}
                      onClick={() => {
                        dispatch(clearError());
                        onAddAuthorisation();
                      }}
                    >
                      New authorisation
                    </Button>
                  </TableToolbarContent>
                </TableToolbar>
                <Table {...getTableProps()}>
                  <TableHead>
                    <TableRow>
                      {headers.map((header: any, i: number) => (
                        <TableHeader
                          {...getHeaderProps({
                            header,
                            isSortable: !!header.isSortable
                          })}
                          colSpan={i === 0 ? 2 : undefined}
                          key={i}
                          isSortHeader={sortingHeader === header.key}
                          sortDirection={sortDirection}
                          onClick={() => onClickHeader(header.key)}
                        >
                          {header.header}
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row: any) => (
                      <React.Fragment key={row.id}>
                        <TableExpandRow {...getRowProps({ row })}>
                          {row.cells.map((cell: any) => (
                            <TableCell key={cell.id}>
                              {renderCell(cell, row, authorisations)}
                            </TableCell>
                          ))}
                        </TableExpandRow>
                        <TableExpandedRow colSpan={headers.length + 1}>
                          <DetailsTable
                            authorisation={authorisations.find(
                              (a) => String(a.id) === row.id
                            )}
                          />
                          <OrderTable
                            onAddOrder={() => onAddOrder(row.id)}
                            orders={
                              authorisations.find(
                                (a) => String(a.id) === row.id
                              )?.orders || []
                            }
                            authorisationId={row.id}
                          />
                        </TableExpandedRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DataTable>
          {rowData.length >= 1 && (
            <Pagination
              onChange={(data) => handlePageChange(data)}
              backwardText="Previous page"
              forwardText="Next page"
              itemsPerPageText="Items per page:"
              page={currentPage}
              pageNumberText="Page Number"
              pageSize={pageSize}
              pageSizes={PageSizes}
              totalItems={totalAuthorisations}
            />
          )}
        </>
      )}
    </>
  );
};
export default ListView;
